/**
 * 🔮 ATTRACTION TEST FLOW - Le Parcours de la Loi d'Attraction
 * 
 * Ce composant gère le parcours OBLIGATOIRE avant d'accéder aux salons Txtofs :
 * 1. Photo de profil (IA, avatar, cadres)
 * 2. Accroche magique (bio)
 * 3. Test de la Loi d'Attraction (Soul + Shadow + Psyche)
 * 4. Activation Mode Clair/Ombre
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Camera, Pen, Brain, Moon, Sun, Lock, 
  Check, ChevronRight, ArrowRight, Heart, Zap, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// Composants des quiz
import { ShadowQuiz } from './shadow/ShadowSins';
import { PassionSelector, SoulQuestionsWizard } from './soul/SoulPassions';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 📊 ÉTAPES DU PARCOURS
const FLOW_STEPS = [
  {
    id: 'photo',
    title: 'Ta Photo',
    subtitle: 'Montre qui tu es !',
    icon: Camera,
    emoji: '📸',
    description: 'Une vraie photo = 10x plus de matchs !',
    requiredField: 'hasPhoto'
  },
  {
    id: 'bio',
    title: 'Ton Accroche',
    subtitle: 'Fais-toi désirer !',
    icon: Pen,
    emoji: '✍️',
    description: 'Une bio qui donne envie de te connaître',
    requiredField: 'hasBio'
  },
  {
    id: 'soul',
    title: 'Tes Passions',
    subtitle: 'Ce qui te fait vibrer',
    icon: Heart,
    emoji: '🌟',
    description: 'La loi d attraction : tu attires ce que tu vibres !',
    requiredField: 'hasPassions'
  },
  {
    id: 'shadow',
    title: 'Ton Ombre',
    subtitle: 'Tes désirs cachés',
    icon: Moon,
    emoji: '🌙',
    description: 'Découvre ton péché véniel... assumé !',
    requiredField: 'hasShadow'
  },
  {
    id: 'psyche',
    title: 'Tes Patterns',
    subtitle: 'Ton inconscient amoureux',
    icon: Brain,
    emoji: '🧠',
    description: 'Pour te matcher avec quelqu un qui te fait grandir',
    requiredField: 'hasPsyche'
  }
];

/**
 * 🎯 Composant principal - Flow d'Attraction
 */
export function AttractionTestFlow({ 
  customer, 
  profile, 
  isDarkMode, 
  onComplete,
  onOpenProfileSetup 
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(null);

  // 🔧 FALLBACK: Récupérer customerId même si customer est undefined
  const getCustomerId = () => {
    if (customer?.id) return customer.id;
    try {
      const savedUser = localStorage.getItem('heartbeats_user');
      if (savedUser) return JSON.parse(savedUser).id;
    } catch (e) {}
    try {
      const savedCustomer = localStorage.getItem('lpep_customer');
      if (savedCustomer) return JSON.parse(savedCustomer).id;
    } catch (e) {}
    return null;
  };
  
  const customerId = getCustomerId();

  // Charger le statut des quiz
  useEffect(() => {
    if (customerId) {
      checkAllStatus();
    }
  }, [customerId, profile]);

  const checkAllStatus = async () => {
    setIsLoading(true);
    try {
      // Vérifier le profil local
      const hasPhoto = profile?.photos?.length > 0 || !!profile?.avatarImage;
      const hasBio = profile?.bio && profile.bio.length >= 10;
      const hasPassions = profile?.passions && Object.values(profile.passions).flat().length >= 3;

      // Vérifier Shadow
      const hasShadow = profile?.shadowProfile?.dominantSin || profile?.dominantSin;

      // Vérifier Psyche via API
      let hasPsyche = false;
      try {
        const psycheRes = await fetch(`${API_URL}/api/psyche/status/${customerId}`);
        const psycheData = await psycheRes.json();
        hasPsyche = psycheData.hasCompleted;
      } catch (e) {
        console.error('Erreur check psyche:', e);
      }

      const newCompleted = {
        photo: hasPhoto,
        bio: hasBio,
        soul: hasPassions,
        shadow: hasShadow,
        psyche: hasPsyche
      };

      setCompletedSteps(newCompleted);

      // Trouver la première étape non complétée
      const firstIncomplete = FLOW_STEPS.findIndex(step => !newCompleted[step.id]);
      setCurrentStep(firstIncomplete === -1 ? FLOW_STEPS.length : firstIncomplete);

      // Si tout est complété, callback
      if (Object.values(newCompleted).every(v => v)) {
        onComplete?.();
      }
    } catch (err) {
      console.error('Erreur check status:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculer le pourcentage de complétion
  const completionPercentage = Math.round(
    (Object.values(completedSteps).filter(Boolean).length / FLOW_STEPS.length) * 100
  );

  // Tous les tests sont-ils complétés ?
  const isFullyCompleted = Object.values(completedSteps).every(v => v);

  // Handler pour compléter le quiz Shadow
  const handleShadowComplete = async (result) => {
    try {
      // Sauvegarder dans le profil
      const response = await fetch(`${API_URL}/api/heartbeats/profile/${customerId}/shadow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      });

      if (response.ok) {
        setCompletedSteps(prev => ({ ...prev, shadow: true }));
        setShowQuiz(null);
        
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 },
          colors: ['#8B5CF6', '#EC4899', '#1a1a2e']
        });

        toast.success('🖤 Ton ombre est révélée !');
        
        // Passer à l'étape suivante
        const nextStep = FLOW_STEPS.findIndex(s => s.id === 'psyche');
        setCurrentStep(nextStep);
      }
    } catch (err) {
      console.error('Erreur save shadow:', err);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Handler pour le quiz Psyche
  const handlePsycheComplete = async (answers) => {
    if (!customerId) {
      toast.error('Erreur: veuillez vous reconnecter');
      return;
    }
    
    try {
      const url = `${API_URL}/api/psyche/quiz/submit/${customerId}`;
      console.log('🔗 Appel API:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });

      console.log('📡 Réponse API:', response.status, response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Données reçues:', data);
        
        setCompletedSteps(prev => ({ ...prev, psyche: true }));
        setShowQuiz(null);

        confetti({
          particleCount: 100,
          spread: 100,
          origin: { y: 0.6 },
          colors: ['#8B5CF6', '#EC4899', '#10B981']
        });

        toast.success('🧠 Profil psychologique complété ! Les portes s\'ouvrent...');
        onComplete?.();
      } else {
        const errorData = await response.text();
        console.error('❌ Erreur API:', errorData);
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('❌ Erreur save psyche:', err);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Affichage du quiz actif
  if (showQuiz === 'shadow') {
    return (
      <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className={`p-4 bg-gradient-to-r from-gray-800 to-black text-white`}>
          <h3 className="font-bold text-lg">🌙 Quiz de Ombre</h3>
          <p className="text-sm text-gray-300">Découvre ton péché véniel...</p>
        </div>
        <ShadowQuiz 
          onComplete={handleShadowComplete}
          onCancel={() => setShowQuiz(null)}
        />
      </div>
    );
  }

  if (showQuiz === 'psyche') {
    return (
      <PsycheQuizWrapper 
        customerId={customerId}
        isDarkMode={isDarkMode}
        onComplete={handlePsycheComplete}
        onCancel={() => setShowQuiz(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} data-testid="attraction-test-flow">
      {/* Header avec progression */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-4"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-bold">Test de la Loi Attraction</span>
        </motion.div>
        
        <h2 className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {isFullyCompleted ? '🎉 Tu es prêt(e) !' : '🔮 Révèle qui tu es vraiment'}
        </h2>
        
        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {isFullyCompleted 
            ? 'Les salons Txtofs t\'attendent...'
            : 'Complete ce parcours pour débloquer les salons'}
        </p>
      </div>

      {/* Barre de progression */}
      <div className="relative">
        <div className={`h-3 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {completionPercentage}% complété
          </span>
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {Object.values(completedSteps).filter(Boolean).length}/{FLOW_STEPS.length} étapes
          </span>
        </div>
      </div>

      {/* Liste des étapes */}
      <div className="space-y-3">
        {FLOW_STEPS.map((step, index) => {
          const isCompleted = completedSteps[step.id];
          const isCurrent = index === currentStep;
          const isLocked = index > currentStep && !isCompleted;
          const StepIcon = step.icon;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`
                relative p-4 rounded-xl border-2 transition-all cursor-pointer
                ${isCompleted 
                  ? 'border-green-500/50 bg-green-500/10' 
                  : isCurrent
                    ? isDarkMode
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-purple-500 bg-purple-50'
                    : isLocked
                      ? isDarkMode
                        ? 'border-gray-700 bg-gray-800/50 opacity-50'
                        : 'border-gray-200 bg-gray-100 opacity-50'
                      : isDarkMode
                        ? 'border-gray-700 bg-gray-800/50'
                        : 'border-gray-200 bg-gray-50'
                }
              `}
              onClick={() => {
                if (isLocked) {
                  toast.error('🔒 Complete les étapes précédentes d\'abord !');
                  return;
                }
                
                if (step.id === 'photo' || step.id === 'bio' || step.id === 'soul') {
                  onOpenProfileSetup?.();
                } else if (step.id === 'shadow') {
                  setShowQuiz('shadow');
                } else if (step.id === 'psyche') {
                  setShowQuiz('psyche');
                }
              }}
            >
              <div className="flex items-center gap-4">
                {/* Icône / Status */}
                <div className={`
                  w-14 h-14 rounded-xl flex items-center justify-center text-2xl
                  ${isCompleted 
                    ? 'bg-green-500 text-white' 
                    : isCurrent
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-200 text-gray-500'
                  }
                `}>
                  {isCompleted ? <Check className="w-7 h-7" /> : step.emoji}
                </div>

                {/* Texte */}
                <div className="flex-1">
                  <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {step.title}
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isCompleted ? '✓ Complété !' : step.description}
                  </p>
                </div>

                {/* Action */}
                <div>
                  {isCompleted ? (
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-500" />
                    </div>
                  ) : isLocked ? (
                    <Lock className={`w-5 h-5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  ) : (
                    <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  )}
                </div>
              </div>

              {/* Badge étape courante */}
              {isCurrent && !isCompleted && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold"
                >
                  À faire ✨
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Bouton d'action principal */}
      {isFullyCompleted ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <Button
            onClick={onComplete}
            className="w-full py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-bold text-lg rounded-xl shadow-lg"
            data-testid="btn-enter-txtofs"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Entrer dans les Salons Txtofs
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      ) : (
        <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            🔮 Complète le test pour débloquer les salons et être matché avec des âmes compatibles !
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * 🧠 Wrapper pour le quiz Psyche
 */
function PsycheQuizWrapper({ customerId, isDarkMode, onComplete, onCancel }) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/psyche/quiz/questions`);
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions);
      }
    } catch (err) {
      console.error('Erreur fetch questions:', err);
      toast.error('Erreur chargement des questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerId) => {
    const newAnswers = { ...answers, [questionId]: answerId };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
    } else {
      // Quiz terminé
      onComplete(newAnswers);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-lg">🧠 Scénarios de Vie</h3>
          <button onClick={onCancel} className="text-white/70 hover:text-white">
            ✕
          </button>
        </div>
        <p className="text-sm text-white/70">Question {currentQuestion + 1}/{questions.length}</p>
        
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className="h-full bg-white rounded-full"
          />
        </div>
      </div>

      {/* Question */}
      {question && (
        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="p-6"
          >
            {/* Scénario */}
            <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
              <p className={`text-sm italic ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                {question.scenario}
              </p>
            </div>

            {/* Question */}
            <h4 className={`font-bold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {question.question}
            </h4>

            {/* Options */}
            <div className="space-y-2">
              {question.options.map((option) => (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.01, x: 5 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleAnswer(question.id, option.id)}
                  className={`
                    w-full p-4 rounded-xl text-left transition-all border
                    ${answers[question.id] === option.id
                      ? 'border-purple-500 bg-purple-500/20'
                      : isDarkMode
                        ? 'border-gray-700 bg-gray-800/50 hover:border-purple-500/50'
                        : 'border-gray-200 bg-gray-50 hover:border-purple-300'
                    }
                  `}
                >
                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                    {option.text}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

/**
 * 🔒 Composant Gate - Vérifie si l'utilisateur peut accéder aux salons
 */
export function TxtofsGate({ customer, profile, isDarkMode, children, onOpenProfileSetup }) {
  const [isEligible, setIsEligible] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 🔧 FIX: Extraire customerId depuis customer ou profile
  const customerId = customer?.id || profile?.customerId;

  useEffect(() => {
    checkEligibility();
  }, [customerId, profile]);

  const checkEligibility = async () => {
    if (!customerId) {
      console.log('⚠️ TxtofsGate: Pas de customerId, skip eligibility');
      setIsEligible(false);
      setIsLoading(false);
      return;
    }

    try {
      // Vérifications locales
      const hasPhoto = profile?.photos?.length > 0 || !!profile?.avatarImage;
      const hasBio = profile?.bio && profile.bio.length >= 10;
      const hasPassions = profile?.passions && Object.values(profile.passions).flat().length >= 3;
      const hasShadow = profile?.shadowProfile?.dominantSin || profile?.dominantSin;

      // Vérification Psyche via API avec timeout
      let hasPsyche = false;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const res = await fetch(`${API_URL}/api/psyche/status/${customerId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        const data = await res.json();
        hasPsyche = data.hasCompleted;
      } catch (e) {
        console.error('Erreur check psyche:', e);
        // En cas d'erreur, on continue sans bloquer
      }

      // Toutes les conditions doivent être remplies
      setIsEligible(hasPhoto && hasBio && hasPassions && hasShadow && hasPsyche);
    } catch (err) {
      console.error('Erreur eligibility check:', err);
      setIsEligible(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!isEligible) {
    return (
      <AttractionTestFlow
        customer={customer}
        profile={profile}
        isDarkMode={isDarkMode}
        onComplete={() => setIsEligible(true)}
        onOpenProfileSetup={onOpenProfileSetup}
      />
    );
  }

  return children;
}

export default AttractionTestFlow;
